import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { emailTemplatesApi, mailboxesApi, smsTemplatesApi } from "./api";
import type {
  CreateEmailTemplatePayload,
  SendTestEmailPayload,
  SmsTemplatePayload,
  UpdateEmailTemplatePayload
} from "./types";

const mailboxKeys = {
  all: ["MAILBOXES"],
  lists: () => [...mailboxKeys.all, "LIST"],
  list: (filters?: Record<string, unknown>) => [
    ...mailboxKeys.lists(),
    filters
  ],
  details: () => [...mailboxKeys.all, "DETAIL"],
  detail: (id: string) => [...mailboxKeys.details(), id]
};

export const useMailboxes = (enabled: boolean = true) =>
  useQuery({
    queryKey: mailboxKeys.lists(),
    queryFn: () => mailboxesApi.list(),
    enabled
  });

// CRM-1165: the account-wide "Make default" toggle is gone — a mailbox is the
// default for the properties explicitly assigned to it.
export const useMailboxPropertyDefaults = (mailboxId: string | null) =>
  useQuery({
    queryKey: [...mailboxKeys.detail(mailboxId ?? ""), "PROPERTY_DEFAULTS"],
    queryFn: () => mailboxesApi.getPropertyDefaults(mailboxId as string),
    enabled: Boolean(mailboxId),
    // Never served from cache: the picker seeds once on open and writes back a
    // REPLACE, and a cached failure would lock the user out of the mailbox.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    retry: 1
  });

export const useSetMailboxPropertyDefaults = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["MAILBOXES", "SET_PROPERTY_DEFAULTS"],
    mutationFn: ({
      mailboxId,
      propertyIds
    }: {
      mailboxId: string;
      propertyIds: number[];
    }) => mailboxesApi.setPropertyDefaults(mailboxId, propertyIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxKeys.all });
      // The property page and inbox composer keep their own cache of the sender.
      queryClient.invalidateQueries({ queryKey: ["PROPERTY_DEFAULT_EMAIL"] });
    },
    onError: (error: unknown) => {
      toast.error(
        getErrorMessage(error, "Failed to update default properties")
      );
    }
  });
};

// SMS Templates

const smsTemplateKeys = {
  all: ["SMS_TEMPLATES"],
  lists: () => [...smsTemplateKeys.all, "LIST"],
  list: (page: number, perPage: number) => [
    ...smsTemplateKeys.lists(),
    page,
    perPage
  ],
  variables: (subjectType: string) => [
    ...smsTemplateKeys.all,
    "VARIABLES",
    subjectType
  ],
  details: () => [...smsTemplateKeys.all, "DETAIL"],
  detail: (id: number) => [...smsTemplateKeys.details(), id]
};

export const useSmsTemplates = (page = 1, perPage = 25) =>
  useQuery({
    queryKey: smsTemplateKeys.list(page, perPage),
    queryFn: () => smsTemplatesApi.list(page, perPage)
  });

export const useSmsTemplate = (id: number) =>
  useQuery({
    queryKey: smsTemplateKeys.detail(id),
    queryFn: () => smsTemplatesApi.get(id),
    enabled: id > 0
  });

export const useSmsVariables = (subjectType = "lead") =>
  useQuery({
    queryKey: smsTemplateKeys.variables(subjectType),
    queryFn: () => smsTemplatesApi.variables(subjectType)
  });

export const useCreateSmsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "CREATE"],
    mutationFn: (payload: SmsTemplatePayload) =>
      smsTemplatesApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.lists() });
    }
  });
};

export const useUpdateSmsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "UPDATE"],
    mutationFn: ({
      id,
      payload
    }: {
      id: number;
      payload: Partial<SmsTemplatePayload>;
    }) => smsTemplatesApi.update(id, payload),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.detail(id) });
    }
  });
};

export const useDuplicateSmsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "DUPLICATE"],
    mutationFn: (id: number) => smsTemplatesApi.duplicate(id),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: smsTemplateKeys.lists() });
      toast.success("SMS template duplicated");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to duplicate SMS template"));
    }
  });
};

export const useDeleteSmsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "DELETE"],
    mutationFn: (id: number) => smsTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.lists() });
    }
  });
};

// Email Templates

const emailTemplateKeys = {
  all: ["EMAIL_TEMPLATES"],
  lists: () => [...emailTemplateKeys.all, "LIST"],
  list: (params?: Record<string, unknown>) => [
    ...emailTemplateKeys.lists(),
    params
  ],
  details: () => [...emailTemplateKeys.all, "DETAIL"],
  detail: (id: number) => [...emailTemplateKeys.details(), id],
  variables: (subjectType: string) => [
    ...emailTemplateKeys.all,
    "VARIABLES",
    subjectType
  ]
};

export const useEmailTemplates = (params?: Record<string, unknown>) =>
  useQuery({
    queryKey: emailTemplateKeys.list(params),
    queryFn: () => emailTemplatesApi.list(params)
  });

export const useEmailTemplate = (id: number) =>
  useQuery({
    queryKey: emailTemplateKeys.detail(id),
    queryFn: () => emailTemplatesApi.get(id),
    enabled: id > 0
  });

export const useEmailVariables = (subjectType = "lead") =>
  useQuery({
    queryKey: emailTemplateKeys.variables(subjectType),
    queryFn: () => emailTemplatesApi.variables(subjectType),
    staleTime: Infinity
  });

export const useCreateEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailTemplateKeys.all, "CREATE"],
    mutationFn: (payload: CreateEmailTemplatePayload) =>
      emailTemplatesApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.lists()
      });
      toast.success("Email template created");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create email template"));
    }
  });
};

export const useUpdateEmailTemplate = (
  id: number,
  options?: { successMessage?: string }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailTemplateKeys.all, "UPDATE"],
    mutationFn: (payload: UpdateEmailTemplatePayload) =>
      emailTemplatesApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.detail(id)
      });
      toast.success(options?.successMessage ?? "Email template updated");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update email template"));
    }
  });
};

const recipientCount = (count: number) =>
  `${count} ${count === 1 ? "recipient" : "recipients"}`;

export const useSendTestEmail = (id: number) =>
  useMutation({
    mutationKey: [...emailTemplateKeys.all, "SEND_TEST"],
    mutationFn: (payload: SendTestEmailPayload) =>
      emailTemplatesApi.sendTest(id, payload),
    onSuccess: ({ sent_count, failed_count }) => {
      toast.success(`Emails sent to ${recipientCount(sent_count)}`);
      if (failed_count > 0)
        toast.error(
          `Failed to send test email to ${recipientCount(failed_count)}. Please try again later`
        );
    },
    onError: () => {
      toast.error("Failed to send test email. Please try again later");
    }
  });

export const useDuplicateEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailTemplateKeys.all, "DUPLICATE"],
    mutationFn: (id: number) => emailTemplatesApi.duplicate(id),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: emailTemplateKeys.lists() });
      toast.success("Email template duplicated");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to duplicate email template"));
    }
  });
};

export const useDeleteEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailTemplateKeys.all, "DELETE"],
    mutationFn: (id: number) => emailTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.lists()
      });
      toast.success("Email template deleted");
    },
    onError: () => {
      toast.error("Failed to delete email template");
    }
  });
};
